import { Component, OnDestroy, ViewChild, OnInit } from "@angular/core";
import { Subscription } from "rxjs/Subscription";
import { UserState } from "../../app/store/reducers/user.reducer";
import { Store } from "@ngrx/store";
import { AppState } from "../../app/models/app.state";
import { DashboardService } from "../../app/services/dashboard.service";
import { TranslateService } from "@ngx-translate/core";
import {
  NavParams,
  Navbar,
  NavController,
  AlertController,
  LoadingController,
  ToastController,
  Events
} from "ionic-angular";
import {
  careTopics,
  conversationTopic,
  wishesLegacyTopic,
  Topic,
  TopicItem,
  PatientHistory,
  IConsultationStatusDetails,
  topicNames,
  wishesLegacyConfig,
  otherWishesLegacyConfig
} from "../../app/models/types";
import { FhirService } from "../../app/services/fhir.service";
import { DiaprofilPatient } from "../../app/models/diaprofil-patient";
import {
  QuestionnaireResponseService,
  OptionObject
} from "@zitelab/ionic-fhir-questionnaires";
import { _i18n_str } from "../../assets/i18n/non-static-ids";
import * as UserActions from "../../app/store/actions/user.actions";
import { DiaProfilService } from "../../app/services/diaprofil.service";
import { diaprofilEnableWhenFunctions } from "../../app/utils/enable-when-functions";
import { EvaluationState } from "../../app/store/reducers/evaluation.reducer";
import { PatientListingPage } from "../patient-listing.page/patient-listing.page";
import { AdminService } from "../../app/services/admin.service";
import { ConfigState } from "../../app/store/reducers/config.reducer";
import { PatientPage } from "../patient.page/patient.page";
import { ModalController } from "ionic-angular/components/modal/modal-controller";
import { PatchProModal } from "../../modals/patch-pro.modal/patch-pro.modal";
import { DiaprofilProTask } from "../../app/models/diaprofil-pro-task";
import {
  EventLoggingService,
  LogEventId
} from "../../app/services/event-logging.service";
import { CurrentConsultationsPage } from "../current-consultations.page/current-consultations.page";
import { KramModal } from "../../modals/kram.modal/kram.modal";
import { AllAnswersModal } from "../../modals/all-answers.modal/all-answers.modal";
import { ConsultationStatusModal } from "../../modals/consultation-status.modal/consultation-status.modal";

@Component({
  selector: "dashboard-page",
  templateUrl: "dashboard.page.html"
})
export class DashboardPage implements OnDestroy, OnInit {
  prevPageName:
    | "PatientPage"
    | "PatientListingPage"
    | "CurrentConsultationsPage";
  patientHistory: PatientHistory = [];
  patientEvaluationTask: fhir.Task = null;
  hcpEvaluationTask: fhir.Task = null;
  hcpItemEvaluationTask: fhir.Task = null;
  userSubscription: Subscription;
  userState: UserState;
  orgConfig: ConfigState;
  consultationStatus: IConsultationStatusDetails = null;
  evaluationSubscription: Subscription;
  orgConfigSubscription: Subscription;
  relItemEvaluations: EvaluationState = [];
  responseResource: fhir.QuestionnaireResponse;
  proTask: DiaprofilProTask;
  patient: DiaprofilPatient;
  careTopics: Topic[] = careTopics;
  topicItems: { [key in topicNames]: TopicItem[] };
  topicsContainer: { topic: Topic; topicItems: TopicItem[] }[] = [];
  conversationTopic: Topic = conversationTopic;
  conversationItem: Partial<TopicItem> = null;
  otherConversationItem: Partial<TopicItem> = null;
  wishesLegacyTopic: Topic = wishesLegacyTopic;
  wishesItem: Partial<TopicItem> = null;
  otherWishesItem: Partial<TopicItem> = null;
  includeLegacyWishesDisplay = false;
  usedMedicineItem: Partial<TopicItem> = null;
  anyMedicineItem: Partial<TopicItem> = null;
  usedMedicine: OptionObject[] = [];
  insulinTypesItem: Partial<TopicItem> = null;
  usedInsulin: OptionObject[] = [];
  medicineUseStatus: { code: string; display: string }[] = [];
  insulinTooltip: string = "";
  anyDeviceUseItem: Partial<TopicItem>;
  whatDeviceUsedItem: Partial<TopicItem> = null;
  usedDevices: OptionObject[] = [];
  whoAnsweredItem: Partial<TopicItem> = null;
  patientName: string = null;
  responseDate: string = null;
  enableOtherHighlightedAnswers: boolean = true;
  enableTrendDisplay: boolean = false;
  showNoteInput: boolean = false;
  showMoreMiscInfo: boolean = false;
  showTrendInfo: boolean = false;
  miscAnswers: {
    [key: string]: {
      label: string;
      value: string;
      colorCode: string;
      iconName: string;
    };
  } = {
    footStatus: {
      label: null,
      value: null,
      colorCode: null,
      iconName: null
    },
    eyeStatus: {
      label: null,
      value: null,
      colorCode: null,
      iconName: null
    }
  };
  diagnosisTimepointAnswer: OptionObject = null;
  colorBlindMode: boolean = true;
  @ViewChild(Navbar) navbar: Navbar;
  constructor(
    public navCtrl: NavController,
    public store: Store<AppState>,
    public dashboard: DashboardService,
    public diaprofil: DiaProfilService,
    public alertCtrl: AlertController,
    public loadingCtrl: LoadingController,
    public toastCtrl: ToastController,
    public modalCtrl: ModalController,
    public questionnaireResponse: QuestionnaireResponseService,
    public fhir: FhirService,
    public translate: TranslateService,
    private navParams: NavParams,
    public events: Events,
    public admin: AdminService,
    private eventLogging: EventLoggingService
  ) {
    this.prevPageName = this.navParams.get("prevPageName") || null;
    this.proTask = (this.navParams.get("proTask") || null) as DiaprofilProTask;
    this.responseResource = this.proTask.response;
    this.patient = (this.navParams.get("patient") || {}) as DiaprofilPatient;
    this.patientHistory = this.patient.patientHistory;
    this.patientName = this.patient.fullName;
    this.responseDate = this.responseResource.authored;
    this.userSubscription = this.store
      .select(state => state.user)
      .subscribe(user => {
        this.userState = user;
        this.colorBlindMode = user.ffColorBlindMode;
      });
    if (this.admin.isFeatureEnabled("ffDiagnosisTimepointInDashboard")) {
      this.findDiagnosisTimepointAnswer();
    }
  }

  ngOnInit() {
    this.orgConfigSubscription = this.store
      .select(state => state.config)
      .subscribe(config => (this.orgConfig = config));
    this.evaluationSubscription = this.store
      .select(state => state.evaluation)
      .subscribe(
        evaluation =>
          (this.relItemEvaluations = evaluation.filter(
            item => item.questionnaireResponseId === this.responseResource.id
          ))
      );
    this.enableTrendDisplay =
      this.patient.getPrecedingResponse(this.responseResource) !== null
        ? true
        : false;
    this.findPatientEvaluationTask();
    this.getHcpEvaluationTask();
    this.dashboard
      .getConsultationStatus(this.responseResource)
      .subscribe(result => (this.consultationStatus = result));
    this.getHcpItemEvaluationTask();

    this.checkForMunicipalityValue();

    this.events.subscribe("clear:itemEvals", params => {
      if (params.responseId === this.responseResource.id) {
        this.showNoteInput = false;
        this.getHcpItemEvaluationTask();
      }
    });
    this.setTopicsContainer();
    const conversationItemWidget = this.dashboard.widgets.find(
      widget => widget.name === "topics"
    );
    this.conversationItem = {
      config: conversationItemWidget,
      answerInfo: this.questionnaireResponse.getItemAnswerInfo(
        conversationItemWidget,
        this.proTask.flattenedAnswers,
        this.proTask.flattenedDisplayAnswers
      )
    };
    const otherConversationWidget = this.dashboard.widgets.find(
      widget => widget.name === "otherTopics"
    );
    this.otherConversationItem = {
      config: otherConversationWidget,
      answerInfo: this.questionnaireResponse.getItemAnswerInfo(
        otherConversationWidget,
        this.proTask.flattenedAnswers,
        this.proTask.flattenedDisplayAnswers
      )
    };

    /**
     * Handling of legacy wishes item
     */
    this.wishesItem = {
      config: wishesLegacyConfig,
      answerInfo: this.questionnaireResponse.getItemAnswerInfo(
        wishesLegacyConfig,
        this.proTask.flattenedAnswers,
        this.proTask.flattenedDisplayAnswers
      )
    };

    /** We do some manual cleaning of values for legacy wishes item */
    const wishesOptionObj = this.wishesItem.answerInfo
      .optionObj as OptionObject;
    const wishesCodes = <any>wishesOptionObj.code;
    if (Array.isArray(wishesCodes)) {
      this.wishesItem.answerInfo = {
        ...this.wishesItem.answerInfo,
        optionObj: wishesCodes.map((code, index) => {
          const optionObj: OptionObject = {
            code: code,
            colorInterpretation: "NONE",
            designation: null,
            index: index,
            i18nName: null,
            display: this.wishesItem.answerInfo.displayAnswer[index]
          };
          return optionObj;
        })
      };
    }

    const otherWishesItemWidget = this.dashboard.widgets.find(
      widget => widget.name === "otherWishes"
    );
    this.otherWishesItem = {
      config: otherWishesLegacyConfig,
      answerInfo: this.questionnaireResponse.getItemAnswerInfo(
        otherWishesLegacyConfig,
        this.proTask.flattenedAnswers,
        this.proTask.flattenedDisplayAnswers
      )
    };
    this.includeLegacyWishesDisplay = this.proTask.questionnaireVersion.startsWith(
      "0."
    ); // Not used after 1.08 implementation, which was the first with version >= 1
    /**
     * End of legacy wishes item handling
     */

    const anyMedicineWidget = this.dashboard.widgets.find(
      widget => widget.name === "anyUseOfBloodSugerMedicine"
    );
    this.anyMedicineItem = {
      config: anyMedicineWidget,
      answerInfo: this.questionnaireResponse.getItemAnswerInfo(
        anyMedicineWidget,
        this.proTask.flattenedAnswers,
        this.proTask.flattenedDisplayAnswers
      )
    };

    // Used medicine
    const usedMedicineWidget = this.dashboard.widgets.find(
      widget => widget.name === "bloodSugarMedicineTypes"
    );
    this.usedMedicineItem = {
      config: usedMedicineWidget,
      answerInfo: this.questionnaireResponse.getItemAnswerInfo(
        usedMedicineWidget,
        this.proTask.flattenedAnswers,
        this.proTask.flattenedDisplayAnswers
      )
    };
    this.usedMedicine = Array.isArray(
      this.usedMedicineItem.answerInfo.optionObj
    )
      ? this.usedMedicineItem.answerInfo.optionObj
      : [this.usedMedicineItem.answerInfo.optionObj];

    // Insulin details
    const insulinTypesWidget = this.dashboard.widgets.find(
      widget => widget.name === "insulinTypes"
    );
    this.insulinTypesItem = {
      config: insulinTypesWidget,
      answerInfo: this.questionnaireResponse.getItemAnswerInfo(
        insulinTypesWidget,
        this.proTask.flattenedAnswers,
        this.proTask.flattenedDisplayAnswers
      )
    };
    this.usedInsulin = Array.isArray(this.insulinTypesItem.answerInfo.optionObj)
      ? this.insulinTypesItem.answerInfo.optionObj
      : [this.insulinTypesItem.answerInfo.optionObj];

    this.medicineUseStatus = this.calculateMedicineUseStatus();
    this.insulinTooltip = this.calculateInsulinTooltip();

    const anyDeviceUseWidget = this.dashboard.widgets.find(
      widget => widget.name === "useOfBloodSugarMeasurementDevice"
    );
    this.anyDeviceUseItem = {
      config: anyDeviceUseWidget,
      answerInfo: this.questionnaireResponse.getItemAnswerInfo(
        anyDeviceUseWidget,
        this.proTask.flattenedAnswers,
        this.proTask.flattenedDisplayAnswers
      )
    } as TopicItem;

    const whatDeviceUsedWidget = this.dashboard.widgets.find(
      widget => widget.name === "whatMethodDoYouUseForMeasuringBloodSugar"
    );
    this.whatDeviceUsedItem = {
      config: whatDeviceUsedWidget,
      answerInfo: this.questionnaireResponse.getItemAnswerInfo(
        whatDeviceUsedWidget,
        this.proTask.flattenedAnswers,
        this.proTask.flattenedDisplayAnswers
      )
    };
    this.usedDevices = Array.isArray(
      this.whatDeviceUsedItem.answerInfo.optionObj
    )
      ? this.whatDeviceUsedItem.answerInfo.optionObj
      : [this.whatDeviceUsedItem.answerInfo.optionObj];

    const miscItemIcons = {
      YES: { iconName: "checkmark-circle", color: "dashboard-item-green" },
      NO: { iconName: "close-circle", color: "dashboard-item-red" },
      DONT_KNOW: { iconName: "radio-button-off", color: "dashboard-item-none" }
    };
    const miscItemInfo = [
      { id: "footStatus", label: "Fødder hos fodterapeut" },
      { id: "eyeStatus", label: "Øjne for diabetesforandringer" }
    ];
    this.miscAnswers = miscItemInfo.reduce((acc, item) => {
      //
      const itemWidget = this.dashboard.widgets.find(
        widget => widget.name === item.id
      );
      const answerInfo = this.questionnaireResponse.getItemAnswerInfo(
        itemWidget,
        this.proTask.flattenedAnswers,
        this.proTask.flattenedDisplayAnswers
      );
      acc[item.id] = {
        label: item.label,
        value: answerInfo.rawAnswer,
        colorCode: answerInfo.colorCode,
        iconName: miscItemIcons.hasOwnProperty(answerInfo.rawAnswer)
          ? miscItemIcons[answerInfo.rawAnswer].iconName
          : "radio-button-off",
        iconColor: miscItemIcons.hasOwnProperty(answerInfo.rawAnswer)
          ? miscItemIcons[answerInfo.rawAnswer].color
          : "dashboard-item-none"
      };
      return acc;
    }, {});
    const whoAnsweredWidget = this.dashboard.widgets.find(
      widget => widget.name === "whoAnswered"
    );
    this.whoAnsweredItem = {
      config: whoAnsweredWidget,
      answerInfo: this.questionnaireResponse.getItemAnswerInfo(
        whoAnsweredWidget,
        this.proTask.flattenedAnswers,
        this.proTask.flattenedDisplayAnswers
      )
    };

    this.eventLogging.saveEvent({
      event_id: LogEventId.OPEN_QUESTIONNAIRE_RESPONSE,
      event_src_location: "DashboardPage",
      patient_fhir_id: this.patient.fhirId,
      patient_full_name: this.patient.fullName,
      parent_task_uuid: this.proTask.task.id,
      qnr_response_uuid: this.proTask.response.id
    });
  }

  backBtnFunction = () => {
    if (this.prevPageName === "PatientPage") {
      this.navCtrl.setRoot(
        PatientPage,
        { patient: this.patient },
        { animate: false }
      );
    } else if (this.prevPageName === "CurrentConsultationsPage") {
      this.navCtrl.setRoot(CurrentConsultationsPage, {}, { animate: false });
    } else {
      this.navCtrl.setRoot(PatientListingPage, { animate: false });
    }
  };

  getHcpEvaluationTask() {
    this.fhir
      .getQuery({
        resourceType: "Task",
        queryParams: {
          code: "|parentTask",
          intent: "HCP_POST_CONSULTATION",
          "based-on": `QuestionnaireResponse/${this.responseResource.id}`,
          "part-of:missing": "true"
        }
      })
      .subscribe(result => {
        if (result.total > 0) {
          this.hcpEvaluationTask = result.entry[0].resource as fhir.Task;
          this.fhir.addResourcesToState({
            resources: [this.hcpEvaluationTask],
            overwrite: true,
            resolveRefs: true
          });
        }
      });
  }

  getHcpItemEvaluationTask() {
    this.fhir
      .getQuery({
        resourceType: "Task",
        queryParams: {
          code: "|parentTask",
          intent: "HCP_ITEM_NOTES",
          "based-on": `QuestionnaireResponse/${this.responseResource.id}`,
          "part-of:missing": "true"
        }
      })
      .subscribe(result => {
        if (result.total > 0) {
          this.hcpItemEvaluationTask = result.entry[0].resource as fhir.Task;
        }
      });
  }

  findPatientEvaluationTask() {
    this.fhir
      .getQuery({
        resourceType: "Task",
        queryParams: {
          code: "|parentTask",
          intent: "PATIENT_POST_CONSULTATION",
          "based-on": `QuestionnaireResponse/${this.responseResource.id}`,
          owner: this.patient.fhirId,
          "part-of:missing": "true",
          _count: 1
        }
      })
      .subscribe(result => {
        if (result.total > 0) {
          this.patientEvaluationTask = result.entry[0].resource as fhir.Task;
        }
      });
  }

  markConsultationAsDone() {
    const data = {
      patient: this.patient,
      proTask: this.proTask,
      userState: this.userState
    };
    const modal = this.modalCtrl.create(ConsultationStatusModal, data, {
      cssClass: "medium-modal",
      enableBackdropDismiss: false
    });
    modal.onDidDismiss(updatedProTask => {
      console.log("DISMISSED ConsultationStatusModal");
      this.proTask = updatedProTask;
      this.dashboard
        .getConsultationStatus(this.proTask.response)
        .subscribe(result => (this.consultationStatus = result));
    });
    modal.present();
  }

  toClinicianEvaluation(parentTask: fhir.Task) {
    const prompt = this.alertCtrl.create({
      title: this.translate.instant(
        "DASHBOARD_PAGE.BEGIN_CLINICIAN_EVALUTION_ALERT.HEADER"
      ),
      message: this.translate.instant(
        "DASHBOARD_PAGE.BEGIN_CLINICIAN_EVALUTION_ALERT.MESSAGE"
      ),
      buttons: [
        {
          text: this.translate.instant(
            "DASHBOARD_PAGE.BEGIN_CLINICIAN_EVALUTION_ALERT.OK_BTN"
          ),
          handler: data => {
            const loading = this.loadingCtrl.create({
              content: this.translate.instant(
                _i18n_str("GENERIC_UI.LOADING_TASK")
              ),
              dismissOnPageChange: true
            });
            loading.present();
            const userReference: fhir.Reference = {
              display: this.userState.userName,
              reference: `${this.userState.userFhir.resourceType}/${this.userState.userFhir.id}`
            };
            const responseReference: fhir.Reference = {
              display: this.responseResource.id,
              reference: `${this.responseResource.resourceType}/${this.responseResource.id}`
            };
            this.diaprofil.goToQuestionnaires({
              parentTask: parentTask,
              sourceRef: userReference,
              authorRef: userReference,
              subjectRef: responseReference,
              sourceNav: {
                pageComponent: DashboardPage,
                navParams: this.navParams
              }
            });
          }
        },
        {
          text: this.translate.instant(
            "DASHBOARD_PAGE.BEGIN_CLINICIAN_EVALUTION_ALERT.CANCEL_BTN"
          ),
          handler: data => {
            //
          }
        }
      ]
    });
    prompt.present();
  }

  ngOnDestroy() {
    this.userSubscription.unsubscribe();
    this.evaluationSubscription.unsubscribe();
    this.orgConfigSubscription.unsubscribe();
  }

  toggleEnableOtherHighlightedAnswers($event) {
    this.enableOtherHighlightedAnswers = this.enableOtherHighlightedAnswers
      ? false
      : true;
  }

  toggleTrendDisplay($event) {
    this.enableTrendDisplay = this.enableTrendDisplay ? false : true;
  }

  toggleShowNoteInput($event) {
    this.showNoteInput = this.showNoteInput ? false : true;
  }

  toggleMoreMiscInfo($event) {
    this.showMoreMiscInfo = this.showMoreMiscInfo ? false : true;
  }

  toggleShowTrendInfo($event) {
    const newValue = this.showTrendInfo ? false : true;
    this.showTrendInfo = newValue;
  }

  triggerSubmitNotesAlert($event) {
    const prompt = this.alertCtrl.create({
      title: this.translate.instant("DASHBOARD_PAGE.SUBMIT_NOTES_ALERT.HEADER"),
      message: this.translate.instant(
        "DASHBOARD_PAGE.SUBMIT_NOTES_ALERT.MESSAGE"
      ),
      buttons: [
        {
          text: this.translate.instant(
            "DASHBOARD_PAGE.SUBMIT_NOTES_ALERT.OK_BTN"
          ),
          handler: data => {
            const loading = this.loadingCtrl.create({
              content: this.translate.instant(
                _i18n_str("GENERIC_UI.SUBMITTING_DATA")
              ),
              dismissOnPageChange: true
            });
            loading.present();
            this.diaprofil.submitItemEvalQuestionnaireResponse(
              { evalItem: this.relItemEvaluations },
              this.responseResource,
              this.patient,
              () => {
                loading.dismissAll();
              }
            );
          }
        },
        {
          text: this.translate.instant(
            "DASHBOARD_PAGE.SUBMIT_NOTES_ALERT.CANCEL_BTN"
          ),
          handler: data => {
            //
          }
        }
      ]
    });
    prompt.present();
  }

  setTopicsContainer() {
    const allTopics = this.careTopics.reduce(
      (acc, topic, index) => {
        acc[topic.id] = this.getTopicItems(topic);
        return acc;
      },
      {
        HEALTH_AND_LIFE_STATUS: null,
        MENTAL_HEALTH: null,
        SYMPTOMS: null,
        DIABETES_IN_EVERYDAY_LIFE: null,
        DIABETES_WORRIES: null,
        SELF_CARE: null,
        BLOOD_SUGAR_REGULATION: null,
        IMPRESSION_OF_MEDICINE: null,
        CONTACT_TREATMENT_PROVIDER: null,
        WISHES: null,
        TOPIC_OF_CONVERSATION: null,
        MISC_IMPORTANT: null
      }
    );
    this.topicItems = allTopics;
    this.topicsContainer = careTopics.map(topic => ({
      topic,
      topicItems: this.topicItems[topic.id]
    }));
    this.topicsContainer = [...this.topicsContainer];
  }

  getTopicItems(topic: Topic): TopicItem[] {
    const topicItems = this.dashboard.widgets
      .filter(widget => widget.valueBasedHealthTopic.topic === topic.id)
      .map(widget => {
        const fullHistory = this.dashboard.getHistoryForWidget(
          this.patientHistory,
          widget
        );
        const curHistory = fullHistory.answers.find(
          elem => elem.source.id === this.responseResource.id
        );
        const trendInfo = curHistory !== undefined ? curHistory.change : null;
        return {
          config: widget,
          answerInfo: this.questionnaireResponse.getItemAnswerInfo(
            widget,
            this.proTask.flattenedAnswers,
            this.proTask.flattenedDisplayAnswers,
            diaprofilEnableWhenFunctions
          ),
          responseResource: this.responseResource,
          fullHistory: fullHistory,
          trendInfo: trendInfo
        };
      });
    return topicItems;
  }

  isEvaluationActiveForPatient() {
    const evalProject = this.orgConfig.evalProject;
    const evalProjectStatus = this.orgConfig.evalProjectParticipationStatus;
    const patientConsent = this.patient.evalConsent;
    return (
      evalProject !== "NONE" &&
      evalProjectStatus === "ACTIVE" &&
      patientConsent === "YES_I_CONSENT"
    );
  }

  isHcpEvaluationDisabled() {
    if (this.hcpEvaluationTask === null) {
      return true;
    } else if (this.hcpEvaluationTask.status === "completed") {
      return true;
    } else {
      return !(
        this.hcpEvaluationTask.owner.reference ===
        `${this.userState.userFhir.resourceType}/${this.userState.userFhir.id}`
      );
    }
  }

  getInvestigationBadgeText(item: {
    label: string;
    value: string;
    colorCode: string;
    iconName: string;
  }) {
    if (item.value === "YES") {
      return "Ja";
    } else if (item.value === "NO") {
      return "Nej";
    } else if (item.value === "DONT_KNOW") {
      return "Ved ikke";
    } else {
      return "Ikke besvaret";
    }
  }

  calculateMedicineUseStatus(): { code: string; display: string }[] {
    const detailedRawAnswers = this.usedMedicine.map(item => item.code);
    const detailedDisplayAnswers = this.usedMedicine.map(item => ({
      code: item.code,
      display: item.display
    }));
    const anyMedicineAnswer = this.anyMedicineItem.answerInfo.rawAnswer;
    let value: { code: string; display: string }[] = [];
    if (anyMedicineAnswer === "NO_ANSWER") {
      if (detailedRawAnswers.includes("NO_ANSWER")) {
        value = [{ code: "NO_ANSWER", display: "Ikke besvaret" }];
      } else if (detailedRawAnswers.includes("NO_MEDICAL_TREATMENT")) {
        value = [{ code: "NO_MEDICAL_TREATMENT", display: "Nej" }];
      } else {
        value = detailedDisplayAnswers;
      }
    } else if (anyMedicineAnswer === "YES") {
      if (detailedRawAnswers.includes("NO_ANSWER")) {
        value = [
          { code: "NONE_SPECIFIED", display: "Ja (mangler svar på hvilken)" }
        ];
      } else {
        value = detailedDisplayAnswers;
      }
    } else {
      value = [{ code: "NO", display: "Nej" }];
    }
    return value;
  }

  calculateInsulinTooltip(): string {
    return this.usedInsulin.map(elem => elem.display).join(", ");
  }

  updateColorBlindSetting() {
    this.store.dispatch(
      new UserActions.UpdateAction({ ffColorBlindMode: !this.colorBlindMode })
    );
  }

  triggerRejectResponseAlert() {
    const prompt = this.alertCtrl.create({
      title: this.translate.instant("REJECT_TASK_ALERT.HEADER"),
      cssClass: "large-alert",
      buttons: [
        {
          text: this.translate.instant("REJECT_TASK_ALERT.CANCEL_BTN"),
          handler: data => {
            //
          }
        },
        {
          text: this.translate.instant("REJECT_TASK_ALERT.OK_BTN"),
          handler: () => {
            this.rejectResponse();
          }
        }
      ]
    });
    prompt.present();
  }

  rejectResponse() {
    this.fhir
      .patchResource({
        resourceType: "QuestionnaireResponse",
        resourceId: this.proTask.response.id,
        operations: [
          {
            op: "replace",
            path: "/status",
            value: "rejected"
          }
        ]
      })
      .subscribe(result => {
        setTimeout(() => {
          this.navCtrl.setRoot(
            PatientPage,
            { patient: this.patient },
            { animate: false }
          );
        }, 1);
      });
  }

  findDiagnosisTimepointAnswer() {
    this.dashboard
      .getDiagnosisTimepointResponse(this.responseResource)
      .subscribe(result => {
        this.diagnosisTimepointAnswer = result;
        if (
          result.code === "WITHIN_LAST_3_MONTHS" ||
          result.code === "WITHIN_LAST_4_TO_6_MONTHS"
        ) {
          const prompt = this.alertCtrl.create({
            title: this.translate.instant(
              "DASHBOARD_PAGE.RECENTLY_DIAGNOSED_PATIENT_ALERT.HEADER"
            ),
            message: this.translate.instant(
              "DASHBOARD_PAGE.RECENTLY_DIAGNOSED_PATIENT_ALERT.CONTENT"
            ),
            cssClass: "large-alert",
            buttons: ["OK"]
          });
          prompt.present();
        }
      });
  }

  triggerProPatchModal($event) {
    const data = {
      patient: this.patient,
      proTask: this.proTask
    };
    const modal = this.modalCtrl.create(PatchProModal, data, {
      cssClass: "large-modal",
      enableBackdropDismiss: false
    });
    modal.onDidDismiss(data => {
      if (data !== undefined) {
        const loading = this.loadingCtrl.create({
          content: this.translate.instant(_i18n_str("GENERIC_UI.LOADING")),
          dismissOnPageChange: true
        });
        loading.present();
        this.responseResource = data.editedResponse;
        this.proTask.response = data.editedResponse; // TODO: Much more handling of updated response
        const extractedAnswers = this.questionnaireResponse.extractAnswersFromQuestionnaireResponse(
          this.responseResource
        );
        const flattenedAnswers = this.questionnaireResponse.flattenQuestionnaireAnswers(
          extractedAnswers
        );
        this.proTask.flattenedAnswers = flattenedAnswers;
        const flattenedDisplayAnswers = this.questionnaireResponse.flattenQuestionnaireAnswers(
          extractedAnswers,
          true
        );
        this.proTask.flattenedDisplayAnswers = flattenedDisplayAnswers;
        this.ngOnInit();
        loading.dismiss();
      }
    });
    modal.present();
  }

  triggerKramModal($event) {
    const data = {
      patient: this.patient,
      proTask: this.proTask
    };
    const modal = this.modalCtrl.create(KramModal, data, {
      cssClass: "kram-modal",
      enableBackdropDismiss: false
    });
    modal.onDidDismiss(data => {
      if (data !== undefined) {
        const loading = this.loadingCtrl.create({
          content: this.translate.instant(_i18n_str("GENERIC_UI.LOADING")),
          dismissOnPageChange: true
        });
        loading.present();
        this.ngOnInit();
        loading.dismiss();
      }
    });
    modal.present();
  }

  triggerAllAnswersModal($event) {
    const data = {
      patient: this.patient,
      proTask: this.proTask
    };
    const modal = this.modalCtrl.create(AllAnswersModal, data, {
      cssClass: "all-answers-modal",
      enableBackdropDismiss: false
    });
    modal.onDidDismiss(data => {
      if (data !== undefined) {
        const loading = this.loadingCtrl.create({
          content: this.translate.instant(_i18n_str("GENERIC_UI.LOADING")),
          dismissOnPageChange: true
        });
        loading.present();
        this.ngOnInit();
        loading.dismiss();
      }
    });
    modal.present();
  }

  goToPatientPage() {
    this.navCtrl.setRoot(
      PatientPage,
      { patient: this.patient },
      { animate: false }
    );
  }

  checkForMunicipalityValue() {
    if (!this.patient.municipality) {
      const alert = this.alertCtrl.create({
        title: "Angivelse af kommune mangler",
        message:
          "<p>Der er endnu ikke angivet i Diaprofil, hvilken kommune patienten bor i.</p><p>Det er derfor ikke muligt at vise evt. relevante kommunale handlemuligheder.</p><p>Kommune kan efterregistreres i patientens stamdata.</p>",
        buttons: ["OK"]
      });
      alert.present();
    }
  }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard.page/dashboard.page.ts